import { useEffect, useRef, useState } from "react";
import ShowMaterialMediaByContentType from "./ShowMaterialMediaByContentType";
import { InlineMath } from "react-katex";
import {
  constructInputWithSpecialExpressionList,
  checkAnswerState,
} from "../../../utilization/CatchtivityUtilization";
import i18n from "../../../language/i18n";
import { IGroupingActivityMaterialProps } from "../../../properties/ActivityProperties";
import { resolveMaterialMediaType } from "../../../utilization/StorageUtilization";

// Design-prototype layout (design system V4): a word pool on top, then the
// groups as a responsive grid of titled dashed-glass bins. Tap a pool item, then tap a
// bin to place it (tap a placed item to send it back). Once graded the pool goes away
// and every item the learner did not place correctly appears as a dashed key chip in
// the bin it belongs to. Prefix: mc-gp2-.
const GroupingActivityMaterialContent = ({
  uniqueValue,
  answerMap,
  materialMap,
  contentMap,
  checkCanAnswerQuestion,
  onChange,
  isPreview,
  showCorrectAnswer,
}: IGroupingActivityMaterialProps) => {
  const [selectedValue, setSelectedValue] = useState<string | null>(null);
  const [displayAnswerMap, setDisplayAnswerMap] = useState<any>(answerMap);

  // Pool order: shuffled once PER ACTIVITY (content-keyed) — a re-render never
  // reverts to the bin-grouped source order, which would leak group membership.
  const shuffle = (array: any) => {
    const c = JSON.parse(JSON.stringify(array));
    for (let i = c.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [c[i], c[j]] = [c[j], c[i]];
    }
    return c;
  };
  const buildList = () => {
    const list: any[] = [];
    Object.keys(materialMap).forEach((k) => {
      for (const v of materialMap[k]) list.push(v);
    });
    return list;
  };
  const materialMapKey = JSON.stringify(materialMap);
  const [shuffledMaterialList, setShuffledMaterialList] = useState<any[]>(() =>
    shuffle(buildList()),
  );
  const shuffleKeyRef = useRef<string>(materialMapKey);
  useEffect(() => {
    if (shuffleKeyRef.current === materialMapKey) return;
    shuffleKeyRef.current = materialMapKey;
    setShuffledMaterialList(shuffle(buildList()));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [materialMapKey]);

  useEffect(() => {
    setDisplayAnswerMap(showCorrectAnswer ? materialMap : answerMap);
  }, [showCorrectAnswer, answerMap, materialMap]);

  const poolList = (() => {
    const placed: string[] = [];
    Object.keys(displayAnswerMap).forEach((k) => {
      (displayAnswerMap[k] || []).forEach((v: string) => placed.push(v));
    });
    return shuffledMaterialList.filter((m) => placed.indexOf(m) === -1);
  })();

  const graded = Boolean(isPreview);
  // Once graded the pool is decorative — it is not tappable, and every item
  // still sitting in it is one the learner never placed. Those now surface in
  // the bin they BELONG to (below), so leaving them in the pool as well would
  // print them twice and still imply unfinished work.
  const showPool = !graded;
  // The key reveal. Anything in a bin's correct list that the learner did not
  // put there — left in the pool, or dropped into the wrong bin — is shown as
  // a dashed "Doğru cevap" chip in the bin it belongs to. Without it, a skipped
  // item was the one item whose answer the learner never got to see.
  // Suppressed for a plain correct-answer reveal (showCorrectAnswer already
  // swaps displayAnswerMap for the key, so every bin is complete and correct).
  const revealKeys = graded && !showCorrectAnswer;
  const missedFor = (key: string): string[] => {
    if (!revealKeys) return [];
    const placedHere = displayAnswerMap[key] || [];
    return (materialMap[key] || []).filter(
      (value: string) => placedHere.indexOf(value) === -1,
    );
  };
  const hasMissed = Object.keys(displayAnswerMap).some(
    (key) => missedFor(key).length > 0,
  );

  const handleSelectItem = (v: string) => {
    if (!checkCanAnswerQuestion() || isPreview) return;
    setSelectedValue((prev) => (prev === v ? null : v));
  };
  const handleDropZoneClick = (key: string) => {
    if (selectedValue !== null) {
      onChange(answerMap, key, selectedValue, null);
      setSelectedValue(null);
    }
  };

  const renderChip = (value: string, key: string) =>
    resolveMaterialMediaType(value, contentMap.type) === "TEXT" ? (
      <>
        {constructInputWithSpecialExpressionList(value).map((part, i) => (
          <span
            key={i}
            className={`${part.isBold ? "mc-bold" : ""} ${part.isUnderline ? "mc-underline" : ""}`}
          >
            {part.isEquation ? <InlineMath math={part.value} /> : part.value}
          </span>
        ))}
      </>
    ) : (
      <ShowMaterialMediaByContentType
        key={key}
        contentType={resolveMaterialMediaType(value, contentMap.type)}
        src={value}
        canFullScreen={false}
      />
    );

  return (
    <div className="mc-root mc-gp2">
      {showPool ? (
        <>
          <div className="mc-gp2-poollabel">{i18n.t("grouping_pool_hint")}</div>
          <div className="mc-gp2-pool">
            {poolList.length === 0 ? (
              <span className="mc-gp2-empty">{i18n.t("pool_empty")}</span>
            ) : (
              poolList.map((v, i) => (
                <button
                  key={i}
                  type="button"
                  className={`mc-gp2-chip${
                    selectedValue === v ? " is-picked" : ""
                  }`}
                  onClick={() => handleSelectItem(v)}
                >
                  {renderChip(v, `${uniqueValue}-pool-${i}`)}
                </button>
              ))
            )}
          </div>
        </>
      ) : null}
      <div className="mc-gp2-bins">
        {Object.keys(displayAnswerMap).map((key, i) => (
          <div
            key={i}
            data-grouping-drop-zone={key}
            onClick={() => handleDropZoneClick(key)}
            className={`mc-gp2-bin${selectedValue ? " is-target" : ""}`}
          >
            <div className="mc-gp2-bintitle">{renderChip(key, `${uniqueValue}-k-${i}`)}</div>
            <div className="mc-gp2-binbody">
            {(displayAnswerMap[key] || []).map((v: string, vi: number) => {
              // V4 (aas__grouped): graded chips recolor green/red with an
              // inline ✓/✕; while solving a placed chip shows ✕ (tap removes).
              const state = !isPreview
                ? null
                : !v || !materialMap[key]
                  ? "EMPTY"
                  : checkAnswerState(materialMap[key], v);
              const stateClass =
                state === "CORRECT"
                  ? " is-correct"
                  : state === "INCORRECT" || state === "EMPTY"
                    ? " is-wrong"
                    : "";
              return (
                <button
                  key={vi}
                  type="button"
                  className={`mc-gp2-placed${stateClass}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!checkCanAnswerQuestion() || isPreview) return;
                    // A pool chip is in hand → tapping anywhere in the bin
                    // (including over an existing chip) DROPS it here; it must
                    // never eject the chip underneath.
                    if (selectedValue !== null) {
                      onChange(answerMap, key, selectedValue, null);
                      setSelectedValue(null);
                      return;
                    }
                    onChange(answerMap, key, null, vi);
                    setSelectedValue(null);
                  }}
                >
                  {renderChip(v, `${uniqueValue}-${key}-${vi}`)}
                  {state
                    ? state === "CORRECT"
                      ? " ✓"
                      : " ✕"
                    : checkCanAnswerQuestion()
                      ? " ✕"
                      : ""}
                </button>
              );
            })}
            {missedFor(key).map((v: string, mi: number) => (
              <span
                key={`missed-${mi}`}
                className="mc-gp2-placed is-key"
                title={i18n.t("activity_correct_answer")}
              >
                {renderChip(v, `${uniqueValue}-${key}-key-${mi}`)}
              </span>
            ))}
            {/* The drop hint is a SOLVING affordance — a graded empty bin is
                not waiting for anything, and once the key chips are in there
                it is not empty either. */}
            {!graded &&
            ((displayAnswerMap[key] || []).length === 0 ||
              selectedValue !== null) ? (
              <span className="mc-gp2-binhint">{i18n.t("please_drop_here")}</span>
            ) : null}
            </div>
          </div>
        ))}
      </div>
      {/* Names the dashed chips, so a reveal can never be misread as something
          the learner placed. Mirrors the MCSA / MCMA graded legends. */}
      {hasMissed ? (
        <div className="mc-gp2-legend">
          <span className="mc-gp2-legend-item">
            <span className="mc-gp2-placed is-key mc-gp2-legend-swatch" />
            {i18n.t("activity_correct_answer")}
          </span>
        </div>
      ) : null}
    </div>
  );
};

export default GroupingActivityMaterialContent;
